import React, { useState, useEffect, useCallback, useRef, lazy, Suspense  } from 'react';
import TranscriptionWithoutDiarization from './components/TranscriptionWithoutDiarization';
import MeetBotJoin from './components/MeetBotJoin';
import MeetingProtocol from './components/MeetingProtocol';
import { renameInTranscriptionState, renameInProtocolState, markSpeakerConfirmed } from './lib/speakerRename';
import DualChannelTranscription from './components/DualChannelTranscription';
import AuthPage from './components/AuthPage';
import ClientAuthPage from './components/public/ClientAuthPage';
import StaffEntryPage from './components/public/StaffEntryPage';
import DepartedScreen from './components/public/DepartedScreen';
import { isClientHost } from './lib/host';
import UserMetrics from './components/UserMetrics';
import TTSPage from './components/TTSPage';
import ProfilePage from './components/ProfilePage';

import AdminPanel from './components/AdminPanel';
import AppSidebar from './components/AppSidebar';
import FeedbackDialog from './components/FeedbackDialog';
import DemoAccessDialog from './components/DemoAccessDialog';
import { FEEDBACK_ENTRY_LABEL, unreadTotals } from './lib/feedbackEntry';
import FeedbackAnswerToast, { shouldShow, readShown, writeShown } from './components/FeedbackAnswerToast';
import { metricsService } from './services/metricsService';
import { billingService } from './services/billingService';
import OrganizationPanel from './components/organization/OrganizationPanel';
import QuotaBanner from './components/QuotaBanner';
import PaymentReturnBanner from './components/billing/PaymentReturnBanner';
import AppTopbar from './components/AppTopbar';
import MobileHeader from './components/MobileHeader';
import { useIsMobile } from './hooks/useIsMobile';
import { useKeyboard } from './hooks/useKeyboard';
import KeyboardDoneButton from './components/KeyboardDoneButton';
import HistoryViewer from './components/HistoryViewer';
import PublicShell from './components/public/PublicShell';
import ClientLanding from './components/public/ClientLanding';
import { authService, DISPLAY_NAME_EVENT } from './services/authService';
import keycloak, { keycloakEnabled } from './keycloak';
import { installKeycloakRefresh } from './lib/kcRefresh';
import { historyService } from './services/historyService';
import { buildStreamingHistoryItem, restoreStreamingState, tabChangeAllowed } from './lib/streamingHistory';
import { planDeleteCleanup, tabAfterItemDelete } from './lib/historyDeleteCleanup';
import FolderPath from './components/history/FolderPath';
import { historyMeetingAt } from './lib/meetingDate';
import { transcriptUpdate } from './lib/historySync';
import { tabChrome } from './lib/tabChrome';
import RecognitionScreen, { RECOGNITION_V2, transcriptText } from './components/recognition/RecognitionScreen';
import { stereoRoleLabel } from './lib/recognition/sources/stereo';
import { renameRecording } from './lib/renameRecording';
import { useDeferredHistorySave } from './hooks/useDeferredHistorySave';
import { shouldAddNewSummary } from './lib/summaryLabel';
import { titleService } from './services/titleService';
import { promptService } from './services/promptService';
import { logHistorySettingsChange } from './services/uiLogger';
import JobDoneToast from './components/JobDoneToast';
import { unseenDone, unseenFailed, stillRunning } from './lib/jobsWatch';
import { BTN_SECONDARY } from './lib/buttonStyles';
import { MessageSquare } from 'lucide-react';
import LegalGate from './components/LegalGate';
import { IS_SAAS } from './config/edition';

const TAB_TITLES = {
  transcription: 'Транскрибация',
  streaming: 'Потоковое распознавание',
  dual: 'АТС (стерео)',
  protocol: 'Протокол',
  metrics: 'Метрики',
  tts: 'Озвучка',
  pricing: 'Тарифы',
  organization: 'Организация',
  profile: 'Личный кабинет',
  admin: 'Администрирование',
};

// Серая подсказка рядом с названием — у каждого экрана, чтобы строка везде
// была одной формы (замечание владельца 24.09.2026).
const TAB_HINTS = {
  transcription: 'Загрузите аудио/видео или вставьте ссылку — получите расшифровку с таймкодами',
  protocol: 'Вставьте транскрипт или текст — получите структурированный протокол встречи',
  dual: 'Загрузите стерео-запись звонка — реплики разделятся по каналам (оператор/клиент)',
  streaming: 'Запишите аудио в реальном времени — текст появляется по мере распознавания',
  metrics: 'Статистика распознавания и использования квоты',
  tts: 'Вставьте текст — получите аудио выбранным голосом',
  organization: 'Состав команды и расход общего пакета',
  profile: 'Тариф, История и данные, подключение агентов',
  admin: 'Пользователи, платформа, биллинг и настройки',
  pricing: 'Выберите пакет минут — оплата картой или по счёту для юрлиц',
};

const StreamingTranscription = lazy(() => import('./components/StreamingTranscription'));
const PRICING_ENABLED = (process.env.REACT_APP_EDITION || 'saas') !== 'onprem';
const PricingPage = PRICING_ENABLED ? lazy(() => import('./components/PricingPage')) : null;

const App = () => {
 const [isLoggedIn, setIsLoggedIn] = useState(false);
 // Ответ bit-auth /departure: владелец отвергнутого ключа уволен — вместо входа
 // показываем экран клиентского Ньютона (DepartedScreen).
 const [departure, setDeparture] = useState(null);
 const [tokenData, setTokenData] = useState(null);
 const [activeTab, setActiveTab] = useState('transcription');
 // Название записи, с которой человек сейчас работает на экране «Распознавание».
 // Шапка без него повторяла пункт меню и не говорила, что на экране.
 const [recordTitle, setRecordTitle] = useState(null);
 const [previousTab, setPreviousTab] = useState('transcription');

 const loadSessionState = (key) => {
   try { return JSON.parse(sessionStorage.getItem(key) || '{}'); } catch { return {}; }
 };

 const [transcriptionState, setTranscriptionState] = useState(() => loadSessionState('transcriptionState'));
 // Живая фаза из прошлой загрузки вкладки — остановленная запись: её сохраняет эффект ниже.
 const [streamingState, setStreamingState] = useState(() => restoreStreamingState(loadSessionState('streamingState')));
 const [protocolState, setProtocolState] = useState(() => loadSessionState('protocolState'));
 const [dualState, setDualState] = useState(() => loadSessionState('dualState'));

 // Возврат с платёжной формы шлюза: ?payment=return&pid=... Открываем «Тарифы»
 // и показываем итог. Параметры сразу убираем из адреса, иначе обновление
 // страницы через час снова покажет «проверяем оплату».
 const [paymentReturn, setPaymentReturn] = useState(false);
 useEffect(() => {
   const params = new URLSearchParams(window.location.search);
   if (params.get('payment') !== 'return') return;
   setPaymentReturn(true);
   setActiveTab('pricing');
   window.history.replaceState({}, '', window.location.pathname);
 }, []);

 useEffect(() => {
   const { file, ...rest } = transcriptionState;
   if (file) rest.fileName = file.name;
   sessionStorage.setItem('transcriptionState', JSON.stringify(rest));
 }, [transcriptionState]);

 useEffect(() => {
   sessionStorage.setItem('streamingState', JSON.stringify(streamingState));
 }, [streamingState]);

 useEffect(() => {
   sessionStorage.setItem('protocolState', JSON.stringify(protocolState));
 }, [protocolState]);

 useEffect(() => {
   const { file, ...rest } = dualState;
   if (file) rest.fileName = file.name;
   sessionStorage.setItem('dualState', JSON.stringify(rest));
 }, [dualState]);

 // Связь завершённой работы с записью в истории: ключ — taskId работы,
 // значение — id записи в bit-history (или 'pending' на время POST).
 // Хранится отдельно от *State, т.к. дочерние компоненты целиком
 // перезаписывают свои state-объекты на каждом рендере (id бы затёрся).
 const [historyLinks, setHistoryLinks] = useState(() => {
   try { return JSON.parse(sessionStorage.getItem('historyLinks') || '{}'); } catch { return {}; }
 });
 useEffect(() => {
   sessionStorage.setItem('historyLinks', JSON.stringify(historyLinks));
 }, [historyLinks]);

 // История по умолчанию включена (bit-auth, с 24.09.2026); здесь false — только
 // до ответа /me/settings и при его сбое: не сохранять надёжнее, чем сохранить
 // вопреки явному «выключено».
 const [historyEnabled, setHistoryEnabled] = useState(false);
 const [historyRetention, setHistoryRetention] = useState('month');

 // Best-effort сохранение в историю: идемпотентно по taskId,
 // оптимистичный лок 'pending' защищает от двойного POST при частых ре-рендерах.
 const autoSaveItem = useCallback(async (taskId, item) => {
   if (!historyEnabled) return;                                       // privacy-гейт: выключено → не сохраняем
   if (!taskId || historyLinks[taskId]) return;                       // идемпотентность по taskId
   setHistoryLinks((prev) => ({ ...prev, [taskId]: 'pending' }));     // оптимистичный лок от гонок
   try {
     const { id } = await historyService.save({ ...item, retention: historyRetention });
     setHistoryLinks((prev) => ({ ...prev, [taskId]: id }));
     setHistoryRefreshKey((k) => k + 1);
   } catch (_) {
     // best-effort: при сбое НЕ откатываем лок — иначе смена historyLinks
     // перезапустит эффект и устроит бесконечный retry. Запись просто не сохранится
     // в этой сессии (транскрипт всё равно виден пользователю).
   }
 }, [historyLinks, historyEnabled, historyRetention]);
 // Распознанное при выключенной Истории доедет в неё, когда её включат.
 const saveRecognized = useDeferredHistorySave(historyEnabled, autoSaveItem);

 // Авто-сохранение завершённой транскрибации.
 useEffect(() => {
   const t = transcriptionState;
   if (!t || t.isProcessing || t.error) return;
   if (!(t.parsedTranscription && t.parsedTranscription.length > 0)) return;
   if (!t.taskId) return;
   autoSaveItem(t.taskId, {
     type: 'transcription',
     title: t.activeFileName || 'Транскрибация',
     transcript: t.rawTranscription || '',
     meta: t.audioDuration ? { duration: t.audioDuration } : null,
   });
 }, [transcriptionState, autoSaveItem]);

 // Догоняющее обновление расшифровки: участников переименовывают ПОСЛЕ того,
 // как запись уехала в Историю, а autoSaveItem идемпотентен по taskId и второй
 // раз не срабатывает. Без этого эффекта на экране имена, а в Истории и в
 // публичной ссылке — «[УЧАСТНИК_1]»: работа человека пропадала молча.
 //
 // Что уже отправлено, помним в ref: держать это в transcriptionState нельзя —
 // дочерний компонент целиком перезаписывает свой state-объект на каждом
 // рендере (та же причина, по которой historyLinks живут отдельно).
 const sentTranscriptsRef = useRef({});
 useEffect(() => {
   const t = transcriptionState;
   if (!t?.taskId || !historyEnabled) return;
   const update = transcriptUpdate({
     recordId: historyLinks[t.taskId],
     text: t.rawTranscription,
     lastSent: sentTranscriptsRef.current[t.taskId],
   });
   if (!update) return;
   // Помечаем ДО запроса: эффект перезапустится на любой смене состояния, а
   // ждать ответа — значит послать второй такой же PATCH.
   sentTranscriptsRef.current[t.taskId] = update.transcript;
   historyService.patch(update.recordId, { transcript: update.transcript })
     .then(() => setHistoryRefreshKey((k) => k + 1))
     .catch(() => {
       // best-effort, как и всё сохранение в Историю: на экране правка есть,
       // а следующая правка пошлёт запрос заново.
       delete sentTranscriptsRef.current[t.taskId];
     });
 }, [transcriptionState, historyLinks, historyEnabled]);

 // Тот же догоняющий PATCH для протокола, открытого ИЗ Истории: там запись
 // существует до всякой задачи (standaloneItemId), taskId нет вовсе, и
 // переименование участников в протоколе иначе тоже не доехало бы до текста
 // записи. Один класс ошибки — одна починка на оба пути.
 useEffect(() => {
   const p = protocolState;
   const recordId = p?.standaloneItemId;
   if (!recordId || !historyEnabled) return;
   const update = transcriptUpdate({
     recordId,
     text: p.rawTranscriptionText || p.transcriptionText,
     lastSent: sentTranscriptsRef.current[recordId],
   });
   if (!update) return;
   sentTranscriptsRef.current[recordId] = update.transcript;
   historyService.patch(update.recordId, { transcript: update.transcript })
     .then(() => setHistoryRefreshKey((k) => k + 1))
     .catch(() => { delete sentTranscriptsRef.current[recordId]; });
 }, [protocolState, historyEnabled]);

 // Авто-сохранение завершённой АТС-записи (стерео).
 // У dualState нет плоского текста — собираем его из segments в фиксированном
 // формате (не зависящем от UI-тумблеров showTimestamps/highlightSpeakers).
 // Имя файла в живой сессии — d.file.name; после rehydration — d.fileName.
 useEffect(() => {
   const d = dualState;
   if (!d || d.isProcessing || d.error) return;
   if (!(d.segments && d.segments.length > 0) || !d.taskId) return;
   const text = d.segments
     .map((s) => `${stereoRoleLabel(s.speaker_role)}: ${s.text}`)
     .join('\n\n');
   autoSaveItem(d.taskId, {
     type: 'dual_channel',
     title: d.file?.name || d.fileName || 'АТС-запись',
     transcript: text,
   });
 }, [dualState, autoSaveItem]);

 // Текст live-записи, уже отправленный в Историю, по sessionId: от него
 // считается догоняющий PATCH ниже.
 const sentLiveRef = useRef({});
 // Авто-сохранение live-записи. У потокового режима нет taskId — бэкенд задачи не
 // заводит, сессия живёт в памяти bit-streaming, — поэтому ключ идемпотентности
 // sessionId, который компонент выдаёт при старте записи. Сохраняем на 'stopped':
 // до остановки текст ещё дописывается.
 useEffect(() => {
   const s = streamingState;
   if (!s?.sessionId) return;
   const item = buildStreamingHistoryItem(s);
   if (!item) return;
   // Первый непустой текст — ровно тот, что уйдёт в POST: autoSaveItem
   // идемпотентен, следующие вызовы он отбросит. От него и считаем догонку.
   if (!(s.sessionId in sentLiveRef.current)) sentLiveRef.current[s.sessionId] = item.transcript;
   autoSaveItem(s.sessionId, item);
 }, [streamingState, autoSaveItem]);

 // Догоняющий текст live-записи: финалы последней фразы приходят уже после
 // «Стоп», в грейс-окне finalize, а autoSaveItem идемпотентен и второй раз не
 // сохраняет — без этого короткая запись уезжала в Историю без хвоста.
 useEffect(() => {
   const s = streamingState;
   if (!s?.sessionId || !historyEnabled) return;
   const item = buildStreamingHistoryItem(s);
   if (!item) return;
   const update = transcriptUpdate({
     recordId: historyLinks[s.sessionId],
     text: item.transcript,
     lastSent: sentLiveRef.current[s.sessionId],
   });
   if (!update) return;
   sentLiveRef.current[s.sessionId] = update.transcript;
   historyService.patch(update.recordId, { transcript: update.transcript })
     .then(() => setHistoryRefreshKey((k) => k + 1))
     .catch(() => { delete sentLiveRef.current[s.sessionId]; });
 }, [streamingState, historyLinks, historyEnabled]);

 // Автоимя live-записи. Запись уже в истории под именем-датой; локальная модель
 // называет тему по первой трети расшифровки, заголовок меняется через PATCH.
 // Фоново и best-effort: не вышло — остаётся имя-дата, пользователю ничего не показываем.
 // Ref, а не historyLinks: попытка ровно одна на запись, иначе эффект перезапустится
 // на смене historyLinks после PATCH и уйдёт по кругу.
 const autoTitledRef = useRef(new Set());
 useEffect(() => {
   const sessionId = streamingState?.sessionId;
   if (!sessionId) return;
   const recordId = historyLinks[sessionId];
   if (!recordId || recordId === 'pending') return;         // запись ещё не сохранена
   if (autoTitledRef.current.has(sessionId)) return;

   autoTitledRef.current.add(sessionId);
   renameRecording(recordId, { history: historyService, titles: titleService })
     .then((title) => { if (title) setHistoryRefreshKey((k) => k + 1); });
 }, [streamingState, historyLinks]);

 // Линковка/сохранение протокола. recordId — id записи в истории:
 // привязка к авто-сохранённой транскрибации (по sourceTaskId) ИЛИ ранее
 // созданная отдельная запись (standaloneItemId). Если запись есть — дописываем
 // summary через PATCH (идемпотентно по содержимому resultText); если нет и нет
 // привязки — создаём отдельную запись один раз и запоминаем её id
 // (последующие регенерации «Подробнее/Короче» → PATCH, а не новые записи).
 // best-effort: при сбое маркер остаётся выставленным — иначе смена protocolState
 // перезапустит эффект и устроит бесконечный retry.
 useEffect(() => {
   const p = protocolState;
   if (!p || !p.resultText) return;
   if (!historyEnabled) return;                                   // privacy-гейт: выключено → не сохраняем
   const summary = {
     template: String(p.selectedPromptId || ''),
     model: String(p.selectedModel || ''),
     text: p.resultText,
     // Кем изменён: сервер по этому полю кладёт прежний текст в версии
     // протокола (bit-history/app/summaries.py). Подпись на экране после
     // открытия из Истории не восстанавливается — только стек в вкладке.
     source: p.changedBy || '',
   };
   const linkedId = p.sourceTaskId ? historyLinks[p.sourceTaskId] : null;
   const recordId = (linkedId && linkedId !== 'pending') ? linkedId : (p.standaloneItemId || null);

   if (recordId) {
     if (p.savedSummaryText === p.resultText) return;               // этот текст уже записан
     const savedText = p.resultText;
     // Первый протокол, сделанный кнопкой «Ещё протокол» в Истории, ложится
     // РЯДОМ с прежними (POST), а не вместо них. Дальнейшие «Подробнее/Короче»
     // в этом же окне переписывают уже его (PATCH) — иначе каждое нажатие
     // плодило бы копию.
     const addNew = !!p.addNewSummary;
     setProtocolState((prev) => ({                                  // лок ДО await
       ...prev, savedSummaryText: savedText, addNewSummary: false,
     }));
     (addNew ? historyService.addSummary(recordId, summary)
             : historyService.patch(recordId, { summary }))
       .then(() => setHistoryRefreshKey((k) => k + 1))
       .catch(() => {});                                            // отказ best-effort, без петли
   } else if (!p.sourceTaskId) {
     if (p.standaloneSavedText === p.resultText) return;            // этот текст уже пытались сохранить
     const savedText = p.resultText;
     setProtocolState((prev) => ({ ...prev, standaloneSavedText: savedText }));  // лок/метка ДО await
     historyService.save({
       type: 'transcription',
       title: p.sourceFileName || 'Протокол',
       transcript: p.transcriptionText || '',
       summary,
       retention: historyRetention,
     })
       .then(({ id }) => {
         // запоминаем id → регенерации идут через PATCH, не плодят записи
         setProtocolState((prev) => ({ ...prev, standaloneItemId: id, savedSummaryText: savedText }));
         setHistoryRefreshKey((k) => k + 1);
         // Вставленный из буфера текст в истории назван «Вставленный текст» — одинаково
         // у всех записей. Называем его по содержанию тем же путём, что и live-запись;
         // запись с именем файла renameRecording отсеет сам, модель не позовёт.
         renameRecording(id, { history: historyService, titles: titleService })
           .then((title) => { if (title) setHistoryRefreshKey((k) => k + 1); });
       })
       .catch(() => {});                                            // отказ: метка стоит → нет петли; повтор при новом resultText
   }
   // linkedId === 'pending' и нет standaloneItemId → ждём pending→real (перезапуск по historyLinks)
 }, [protocolState, historyLinks, historyEnabled, historyRetention]);

 const [historyRefreshKey, setHistoryRefreshKey] = useState(0);
 // Обращение можно послать с любого экрана, поэтому окно живёт в App, а не во вкладке.
 const [feedbackOpen, setFeedbackOpen] = useState(false);
 const [demoOpen, setDemoOpen] = useState(false);
 // Непрочитанные решения по обращениям. Опрашиваем редко: цифра меняется, только
 // когда дежурный или человек проставил ответ, а это событие редкое.
 // Состоит ли человек в организации — от этого зависит пункт меню «Организация».
 // Спрашиваем bit-billing один раз на вход; в издании onprem его нет.
 // Перезапрашиваем и при переходе в кабинет/тарифы/организацию, и после
 // возврата с оплаты: организацию заводит оплата тарифа, а в состав добавляют
 // и исключают посреди сессии — один запрос на вход оставлял меню устаревшим.
 const [hasOrganization, setHasOrganization] = useState(false);
 const [isOrgOwner, setIsOrgOwner] = useState(false);
 const orgCheckTab = ['profile', 'pricing', 'organization'].includes(activeTab) ? activeTab : null;
 useEffect(() => {
   if (!tokenData?.email || !PRICING_ENABLED) { setHasOrganization(false); return undefined; }
   let alive = true;
   billingService.getMyOrganization()
     .then((d) => {
       if (!alive) return;
       setHasOrganization(!!d?.organization);
       setIsOrgOwner(!!d?.organization?.is_owner);
     })
     // Биллинг не ответил — меню не трогаем: пропавший пункт хуже устаревшего.
     .catch(() => {});
   return () => { alive = false; };
 }, [tokenData?.email, orgCheckTab, paymentReturn]);
 const onMembershipChange = useCallback((has) => setHasOrganization(has), []);
 // Стабильная ссылка: ProfilePage по ней решает, спрашивать ли организацию.
 const openOrganizationTab = useCallback(() => setActiveTab('organization'), []);
 const [unreadFeedback, setUnreadFeedback] = useState(0);
 // Уведомление «ответили» — один раз на непрочитанное, а не на каждый F5.
 const [answerToast, setAnswerToast] = useState(0);
 const refreshUnreadFeedback = useCallback(() => {
   if (!tokenData?.email) return;
   metricsService.getUnreadFeedback()
     .then((d) => {
       // На кнопке — решения вместе с сообщениями «Что нового»; всплывашка
       // «ответили» — только по решениям.
       const { answers: count, total } = unreadTotals(d);
       setUnreadFeedback(total);
       // Прочитал все — всплывашка «ответили» гаснет тоже, а не возвращается
       // после закрытия окна обращений.
       if (count === 0) setAnswerToast(0);
       else if (shouldShow(count, readShown(window.sessionStorage))) setAnswerToast(count);
     })
     .catch(() => {});   // счётчик — украшение, его отказ не должен ничего ломать
 }, [tokenData?.email]);
 useEffect(() => {
   refreshUnreadFeedback();
   const t = setInterval(refreshUnreadFeedback, 5 * 60 * 1000);
   return () => clearInterval(t);
 }, [refreshUnreadFeedback]);
 const [viewingItem, setViewingItem] = useState(null);
 // Папки Истории (их читает и сообщает наверх боковая панель; null — у учётки
 // папок нет, тогда экраны выглядят как раньше) и запрос панели открыть папку.
 const [folders, setFolders] = useState(null);
 const [openFolderReq, setOpenFolderReq] = useState({ id: null, seq: 0 });
 // Запрос сброса экрана распознавания после удаления его записи: счётчик и ключ задачи.
 // Новый экран решает сам, можно ли (идущую работу не трогает), прежний пересоздаётся по key.
 const [recognitionResetKey, setRecognitionResetKey] = useState(0);
 // Экран «Протокол» держит текст в своём состоянии: после удаления записи его надо
 // пересоздать, иначе следующее действие на нём сохранило бы удалённое новой записью.
 const [protocolResetKey, setProtocolResetKey] = useState(0);
 const [recognitionResetTaskId, setRecognitionResetTaskId] = useState(null);
 // Запись открыта из выдачи поиска: {query, field, summaryId, seq}. Из обычного
 // списка — null. `seq` меняет key просмотрщика на каждое открытие из поиска:
 // та же запись по другому запросу открывается на новом найденном.
 const [viewingSearch, setViewingSearch] = useState(null);
 const searchSeqRef = useRef(0);

 // Мобильный каркас (≤ 768px, «Бумага»): панель — шторкой слева по кнопке
 // меню, остаток минут — в шапке. Шторка закрывается сама, как только выбран
 // раздел или запись: иначе она оставалась бы поверх того, что открыли.
 const isMobile = useIsMobile();
 const [navOpen, setNavOpen] = useState(false);
 const [quotaChip, setQuotaChip] = useState(null);
 // Ширина боковой панели на десктопе (её тянут мышью, AppSidebar помнит её
 // сам). Нужна здесь ради плашки «Идёт обработка», которая стоит правее панели.
 const [sidebarWidth, setSidebarWidth] = useState(null);
 // Клавиатура на телефоне: каркас сжимается до видимой над ней области, а всё,
 // кроме поля и его текста, прячется (.kbd-hide в index.css).
 const keyboard = useKeyboard(isMobile);
 useEffect(() => { setNavOpen(false); }, [activeTab, viewingItem]);

 // Уход из «Распознавания» посреди записи с микрофона её обрывает — переспрашиваем.
 // Через эту проверку идут все переходы, которые делает человек: меню разделов,
 // запись из Истории, профиль, админка, тарифы.
 const canLeaveTo = (t) => tabChangeAllowed(activeTab, t, streamingState, { historyEnabled });

 const openProfile = () => {
   if (!canLeaveTo('profile')) return;
   if (activeTab !== 'profile') setPreviousTab(activeTab);
   setActiveTab('profile');
 };

 const openAdmin = () => {
   if (!canLeaveTo('admin')) return;
   if (activeTab !== 'admin') setPreviousTab(activeTab);
   setActiveTab('admin');
 };

 // Загружаем настройку приватности один раз после входа.
 useEffect(() => {
   if (!isLoggedIn) return;
   let alive = true;
   promptService.getSettings()
     .then((s) => { if (!alive) return; setHistoryEnabled(!!s.history_enabled); if (s.history_retention) setHistoryRetention(s.history_retention); })
     .catch(() => { if (alive) setHistoryEnabled(false); });   // fail-safe приватно
   return () => { alive = false; };
 }, [isLoggedIn]);

 // Оптимистичное обновление настройки + откат к серверному значению при сбое.
 const updateHistorySettings = useCallback(async (fields) => {
   if ('history_enabled' in fields) setHistoryEnabled(fields.history_enabled);
   if ('history_retention' in fields) setHistoryRetention(fields.history_retention);
   try {
     await promptService.updateSettings(fields);
     logHistorySettingsChange(fields, historyRetention);
     setHistoryRefreshKey((k) => k + 1);
   } catch (_) {
     promptService.getSettings().then((s) => {
       setHistoryEnabled(!!s.history_enabled);
       if (s.history_retention) setHistoryRetention(s.history_retention);
     }).catch(() => {});
   }
 }, [historyRetention]);

 // Перечитать настройку Истории (после изменений в профиле) + обновить сайдбар.
 const reloadHistorySettings = useCallback(() => {
   promptService.getSettings()
     .then((s) => { setHistoryEnabled(!!s.history_enabled); if (s.history_retention) setHistoryRetention(s.history_retention); })
     .catch(() => {});
   setHistoryRefreshKey((k) => k + 1);
 }, []);

 // Задания завершителя (bit-history/app/jobs.py): запись, доделанная без
 // вкладки, показывается тостом «Готово» один раз. Задачи, которые вела сама
 // вкладка, помним по ключу — их тост не дублирует сохранение на экране.
 const ownTasksRef = useRef(new Set());
 const [jobToast, setJobToast] = useState(null);
 // Сколько задач ещё считается без вкладки — строка «Идёт обработка · N».
 const [runningJobs, setRunningJobs] = useState(0);
 const seenDoneRef = useRef(new Set());
 const refreshJobs = useCallback(() => {
   if (!tokenData?.email || !historyEnabled) return;
   historyService.listJobs()
     .then((jobs) => {
       const own = Array.from(ownTasksRef.current);
       setRunningJobs(stillRunning(jobs).length);
       // Задачи, которые вела сама вкладка, человек видел на экране —
       // отмечаем просмотренными, чтобы после перезагрузки они не пришли
       // тостом заново.
       jobs.filter((j) => own.includes(j.task_id) && !j.seen_at
                          && (j.status === 'done' || j.status === 'failed'))
         .forEach((j) => { seenDoneRef.current.add(j.id); historyService.markJobSeen(j.id).catch(() => {}); });
       const fresh = [...unseenDone(jobs, own), ...unseenFailed(jobs, own)]
         .filter((j) => !seenDoneRef.current.has(j.id));
       if (fresh.length === 0) return;
       // Доделанное без нас уже лежит в Истории — список должен это увидеть.
       if (fresh.some((j) => j.status === 'done')) setHistoryRefreshKey((k) => k + 1);
       setJobToast((cur) => cur || fresh[0]);
     })
     .catch(() => {});   // уведомление — украшение, его отказ ничего не ломает
 }, [tokenData?.email, historyEnabled]);
 useEffect(() => {
   refreshJobs();
   const t = setInterval(refreshJobs, 30 * 1000);
   const onVisible = () => { if (document.visibilityState === 'visible') refreshJobs(); };
   document.addEventListener('visibilitychange', onVisible);
   return () => { clearInterval(t); document.removeEventListener('visibilitychange', onVisible); };
 }, [refreshJobs]);
 const closeJobToast = useCallback(() => {
   setJobToast((job) => {
     if (job) {
       seenDoneRef.current.add(job.id);
       historyService.markJobSeen(job.id).catch(() => {});
     }
     return null;
   });
 }, []);
 // Регистрация задачи под завершитель — сразу после старта, чтобы закрытая
 // вкладка ничего не потеряла. При выключенной Истории регистрировать нечего:
 // сервер всё равно ответит 403, а запись человек не хотел.
 const registerJob = useCallback(({ taskId, service, title, meta }) => {
   ownTasksRef.current.add(taskId);
   if (!historyEnabled || !taskId) return;
   historyService.registerJob({ taskId, service, title, meta }).catch(() => {});
 }, [historyEnabled]);

 // Имя сохранили (профиль, форма «Позвать бота») — кнопка меню подписана им.
 useEffect(() => {
   const onName = (e) => setTokenData((td) => (td ? { ...td, display_name: e.detail } : td));
   window.addEventListener(DISPLAY_NAME_EVENT, onName);
   return () => window.removeEventListener(DISPLAY_NAME_EVENT, onName);
 }, []);

 // Корзина в панели Истории. Нажали — открытая запись закрывается сразу (возврат на
 // прежнюю вкладку); «Отменить» экран не возвращает, человек откроет запись снова.
 const onItemDeleteStart = (id) => {
   if (viewingItem?.id !== id) return;
   setViewingItem(null);
   setViewingSearch(null);
   // viewingItem живёт и после ухода с вкладки: с другой вкладки не прыгаем.
   setActiveTab(tabAfterItemDelete(activeTab, previousTab));
 };

 // Удаление состоялось: экраны «Распознавание» и «Протокол», показывающие эту
 // запись, очищаются (решение — lib/historyDeleteCleanup.js). Экран распознавания
 // держит результат у себя, поэтому после сброса состояния он ещё и пересоздаётся.
 const onItemDeleted = (id) => {
   const plan = planDeleteCleanup(id, {
     historyLinks, transcriptionState, protocolState,
     keepKeys: [dualState?.taskId, streamingState?.sessionId].filter(Boolean),
   });
   if (plan.resetTranscription) {
     // taskId здесь — последний сохранённый файл, он мог остаться от прошлой записи,
     // пока на экране идёт новая: экран пересоздавать нельзя, он сбросится сам,
     // если показывает готовый результат (RecognitionScreen, resetRequest).
     const deletedTaskId = transcriptionState.taskId;
     setTranscriptionState({});
     if (!RECOGNITION_V2) setRecordTitle(null);
     setRecognitionResetTaskId(deletedTaskId);
     setRecognitionResetKey((k) => k + 1);
   }
   if (plan.resetProtocol) {
     setProtocolState({});
     setProtocolResetKey((k) => k + 1);
   }
   if (plan.nextLinks !== historyLinks) setHistoryLinks(plan.nextLinks);
 };

 // «История» и папка в пути к записи: просим панель открыть список; на телефоне
 // панель — шторка, её открываем сами.
 const requestOpenFolder = (id) => {
   setOpenFolderReq((r) => ({ id, seq: r.seq + 1 }));
   if (isMobile) setNavOpen(true);
 };

 // Перенос открытой записи из пути в верхней строке. Отказ уходит в FolderPath,
 // он покажет текст рядом со строкой.
 const moveViewingItem = async (folderId) => {
   const id = viewingItem.id;
   await historyService.patch(id, { folder_id: folderId });
   setViewingItem((prev) => (prev && prev.id === id ? { ...prev, folder_id: folderId } : prev));
   setHistoryRefreshKey((k) => k + 1);
 };

 const createFolderAndMoveViewing = async (name) => {
   const created = await historyService.createFolder(name);
   // Новая папка нужна пути сразу: панель узнает о ней только после перечитывания,
   // и до тех пор запись читалась бы как «без папки».
   setFolders((prev) => (prev && !prev.some((f) => f.id === created.id) ? [...prev, created] : prev));
   await moveViewingItem(created.id);
 };

 const folderPath = (folders !== null && viewingItem) ? (
   <FolderPath
     key={viewingItem.id}
     compact={isMobile}
     folders={folders}
     folderId={viewingItem.folder_id || null}
     onOpenAll={() => requestOpenFolder(null)}
     onOpenFolder={requestOpenFolder}
     onMove={moveViewingItem}
     onCreateAndMove={createFolderAndMoveViewing}
   />
 ) : null;

 // Имя открытой записи для верхней строки: на распознавании его сообщает экран,
 // на протоколе — имя источника, пока транскрипт загружен (tabChrome).
 const chromeRecordTitle = activeTab === 'transcription'
   ? recordTitle
   : (activeTab === 'protocol' && protocolState?.transcriptionText)
     ? (protocolState.sourceFileName || null)
     : null;

 const openHistoryItem = async (id, search = null) => {
   if (!canLeaveTo('history')) return;
   try {
     const item = await historyService.get(id);
     setViewingItem(item);
     setViewingSearch(search?.query ? { ...search, seq: ++searchSeqRef.current } : null);
     if (activeTab !== 'history') setPreviousTab(activeTab);
     setActiveTab('history');
   } catch (_) {
     // best-effort
   }
 };

 // Переход со страницы ссылки после «Сохранить в Историю»: ?history=<id>.
 // Открываем запись, когда вход подтверждён, и убираем параметр из адреса.
 useEffect(() => {
   if (!isLoggedIn) return;
   const id = new URLSearchParams(window.location.search).get('history');
   if (!id) return;
   window.history.replaceState({}, '', window.location.pathname);
   openHistoryItem(id);
   // eslint-disable-next-line react-hooks/exhaustive-deps
 }, [isLoggedIn]);

 useEffect(() => {
   // DEV MOCK: пропускаем авторизацию для локальной разработки
   if (process.env.NODE_ENV === 'development' && !process.env.REACT_APP_AUTH_API_URL) {
     setIsLoggedIn(true);
     setTokenData({
       access: 'granted', role: 'admin', email: 'dev@local',
       display_name: 'Dev', time_left: '999 days',
     });
     return;
   }
   const checkAuth = async () => {
     if (keycloakEnabled && keycloak && keycloak.authenticated) {
       try {
         const data = await authService.checkKeycloakSession();
         setIsLoggedIn(true);
         setTokenData(data);
         // Фоновое продление токена (раз в 60 с, если жить осталось < 70 с), плюс по
         // возвращению во вкладку и по сигналу самого Keycloak — таймер в скрытой
         // вкладке троттлится. Сбой сети не разлогинивает, пока жив refresh-токен:
         // иначе обрыв Wi-Fi посреди живой записи выкидывал бы из неё (lib/kcRefresh.js).
         installKeycloakRefresh(keycloak, handleLogout);
         return;
       } catch (e) {
         // Уволенному — экран клиентского Ньютона, сессию не рвём: «Выйти» на экране.
         if (e.departure) { setDeparture(e.departure); return; }
         console.error('Keycloak session check failed:', e);
         authService.rememberLoginNotice(e.message);
         handleLogout();
         return;
       }
     }
     if (authService.isLoggedIn()) {
       try {
         const token = authService.getToken();
         const data = await authService.checkToken(token);
         setIsLoggedIn(true);
         setTokenData(data);
       } catch (error) {
         // Ключ уволенного не стираем: открыв Ньютон снова, он снова увидит экран.
         if (error.departure) { setDeparture(error.departure); return; }
         console.error('Error checking auth:', error);
         handleLogout();
       }
     }
   };
   checkAuth();
 }, []);

 // Переименование участника применяем к обоим состояниям сразу: текст
 // расшифровки лежит и во вкладке транскрибации, и в экране протокола, а
 // вкладки размонтируются при переключении. Origin — чтобы не применить
 // правку дважды тому, кто её уже сделал у себя.
 const renameSpeakers = (map, origin) => {
   if (origin !== 'transcription') setTranscriptionState((prev) => renameInTranscriptionState(prev, map));
   if (origin !== 'protocol') setProtocolState((prev) => renameInProtocolState(prev, map));
 };

 // Подтверждение узнанного участника — тоже общее для обоих экранов.
 const confirmSpeaker = (speakerId, origin) => {
   if (origin !== 'transcription') setTranscriptionState((prev) => markSpeakerConfirmed(prev, speakerId));
   if (origin !== 'protocol') setProtocolState((prev) => markSpeakerConfirmed(prev, speakerId));
 };

 // meetingAt — начало встречи (ISO), если оно известно: «Записать сейчас» знает его,
 // у загруженного файла его нет — время файла часто равно моменту скачивания.
 const onOpenProtocol = (text, rawText, sourceFileName, sourceTaskId, speakerIdByLabel,
                         recognizedSpeakers, meetingAt) => {
   setProtocolState(prev => ({
     ...prev,
     transcriptionText: text,
     rawTranscriptionText: rawText || text,
     sourceFileName: sourceFileName || '',
     sourceTaskId: sourceTaskId || null,
     meetingAt: meetingAt || null,
     // Карта «метка на экране → метка диаризации» для «запомнить голос» в Протоколе
     speakerIdByLabel: speakerIdByLabel || {},
     recognizedSpeakers: recognizedSpeakers || [],
     resultText: '',
     savedSummaryText: null,
     standaloneSavedText: null,
     standaloneItemId: null,
     protocols: [],
     activeProtocolKey: null,
     addNewSummary: false,
   }));
   setActiveTab('protocol');
 };

 // Протокол по записи Истории: то же окно протокола, но исходник — уже
 // сохранённая расшифровка. Ключевое здесь — standaloneItemId: запись УЖЕ
 // существует, и эффект сохранения выше допишет протокол в неё через PATCH,
 // а не заведёт рядом вторую с тем же текстом.
 // savedSummaryText ставим равным готовому протоколу: иначе открытие записи с
 // протоколом сразу считалось бы «появился новый текст» и уходило бы лишним
 // PATCH тем же содержимым. Перегенерация даст другой текст и сохранится.
 const onProtocolFromHistory = (item) => {
   if (!item || !item.transcript) return;
   const existing = (item.summary && item.summary.text) || '';
   // Текст записи уже лежит на сервере ровно таким — помечаем отправленным,
   // иначе догоняющий PATCH выше сработает на самом открытии и пошлёт то же
   // самое (та же ловушка, что с savedSummaryText ниже).
   sentTranscriptsRef.current[item.id] = item.transcript;
   setProtocolState((prev) => ({
     ...prev,
     transcriptionText: item.transcript,
     rawTranscriptionText: item.transcript,
     sourceFileName: item.title || '',
     // sourceTaskId пуст намеренно: привязка к записи идёт через
     // standaloneItemId, а не через карту historyLinks задачи транскрибации.
     sourceTaskId: null,
     meetingAt: historyMeetingAt(item),
     speakerIdByLabel: {},
     recognizedSpeakers: [],
     resultText: existing,
     savedSummaryText: existing || null,
     standaloneSavedText: null,
     standaloneItemId: item.id,
     // Все протоколы записи, а не только выбранный: экран показывает их
     // вкладками. Ключ — серверный id, чтобы вкладка пережила переоткрытие.
     protocols: (Array.isArray(item.summaries) ? item.summaries : [])
       .filter((sm) => sm && sm.text)
       .map((sm) => ({
         key: String(sm.id),
         text: sm.text,
         promptId: sm.template || null,
         model: sm.model || 'llama',
         at: sm.created_at || null,
       })),
     activeProtocolKey: (item.summary && item.summary.id) ? String(item.summary.id) : null,
     // У записи уже есть протоколы — новый добавляем рядом, старые не трогаем.
     addNewSummary: shouldAddNewSummary(item),
   }));
   setActiveTab('protocol');
 };

const handleAuth = async (token) => {
  try {
    const data = await authService.checkToken(token);
    setIsLoggedIn(true);
    setTokenData(data);
    return data; // Возвращаем данные в случае успеха
  } catch (error) {
    if (error.departure) setDeparture(error.departure);
    console.error('Error during login:', error);
    // Пробрасываем ошибку обратно в AuthPage для отображения пользователю
    throw error;
  }
};

 const handleLogout = () => {
   authService.logout();
   setIsLoggedIn(false);
   setTokenData(null);
   // Следующий вошедший не должен оказаться на чужой вкладке — например,
   // в «Организации», которой у него нет.
   setActiveTab('transcription');
   setTranscriptionState({});
   setStreamingState({});
   setProtocolState({});
   setDualState({});
 };

 // Корень до логина — витрина: описание сервиса, тарифы, порядок оплаты и возврата,
 // контакты и реквизиты. Форма входа приезжает в неё слотом и остаётся прежней.
 if (departure && !isLoggedIn) {
   return (
     <DepartedScreen
       info={departure}
       onLeave={() => { setDeparture(null); handleLogout(); }}
     />
   );
 }

 if (!isLoggedIn) {
   // На клиентском домене в слот идёт клиентский вход, а не экран сотрудников:
   // иначе клиент с протухшим ключом доступа упирался бы в «Войти через AI
   // Platform» и «ключ доступа» на своём же домене, а кнопка SSO вела бы на
   // Keycloak с незарегистрированным redirect_uri.
   // Витрина — клиентам. Сотруднику она бесполезна: у него внутренние квоты,
   // а не тариф за 4 500 ₽, и вход он ищет каждый день.
   if (!isClientHost()) {
     return (
       <PublicShell nav={[]}>
         <StaffEntryPage authSlot={<AuthPage onAuth={handleAuth} />} />
       </PublicShell>
     );
   }
   return (
     <PublicShell>
       <ClientLanding authSlot={<ClientAuthPage onAuth={handleAuth} />} />
     </PublicShell>
   );
 }

 // recordId сохранённой записи протокола: привязка к авто-сохранённой
 // транскрибации (по sourceTaskId) ИЛИ отдельная запись (standaloneItemId).
 // Используется для кнопки «Поделиться» на экране протокола.
 const protocolRecordId = (() => {
   const p = protocolState;
   if (!p) return null;
   const linkedId = p.sourceTaskId ? historyLinks[p.sourceTaskId] : null;
   return (linkedId && linkedId !== 'pending') ? linkedId : (p.standaloneItemId || null);
 })();

 return (
   // h-screen — запас для браузеров без dvh; где dvh есть, высота не прыгает
   // вместе с адресной строкой телефона.
   <div className="h-screen supports-[height:100dvh]:h-dvh flex flex-row bg-paper-bg overflow-hidden"
        data-kbd={keyboard.open ? 'open' : undefined}
        style={{
          ...(sidebarWidth ? { '--sidebar-w': `${sidebarWidth}px` } : {}),
          ...(keyboard.open
            ? { position: 'fixed', left: 0, right: 0, top: keyboard.top, height: keyboard.height }
            : {}),
        }}>
     {isMobile && keyboard.open && <KeyboardDoneButton />}
     {isMobile && navOpen && (
       <div className="fixed inset-0 z-40 bg-black/30" data-testid="nav-backdrop"
            onClick={() => setNavOpen(false)} />
     )}
     {/* На телефоне панель не размонтируется, а уезжает за край: она же грузит
         Историю и остаток, и шапке нужна её цифра, пока шторка закрыта. */}
     <div
       className={isMobile
         ? `fixed inset-y-0 left-0 z-50 w-[310px] max-w-[85vw] bg-paper-bg rounded-r-[28px]
            shadow-[12px_0_40px_rgba(0,0,0,.15)] transition-[transform,visibility] duration-200
            ${navOpen ? 'translate-x-0 visible' : '-translate-x-full invisible'}`
         : 'contents'}
       data-testid="nav-drawer"
     >
     <AppSidebar
       mobile={isMobile}
       drawerOpen={navOpen}
       onQuotaChange={setQuotaChip}
       onWidthChange={setSidebarWidth}
       activeTab={activeTab}
       // Закрываем шторку и на повторном нажатии того же раздела: эффект выше
       // смены вкладки не увидит, и шторка осталась бы открытой.
       onTabChange={(t) => { if (!canLeaveTo(t)) return; setNavOpen(false); setActiveTab(t); }}
       isAdmin={tokenData?.role === 'admin'}
       role={tokenData?.role}
       email={tokenData?.email}
       displayName={tokenData?.display_name}
       onOpenProfile={() => { setNavOpen(false); openProfile(); }}
       onOpenAdmin={() => { setNavOpen(false); openAdmin(); }}
       onLogout={handleLogout}
       onOpenFeedback={() => { setNavOpen(false); setFeedbackOpen(true); }}
       onOpenDemo={() => { setNavOpen(false); setDemoOpen(true); }}
       unreadFeedback={unreadFeedback}
       onOpenHistoryItem={(id, search) => { setNavOpen(false); openHistoryItem(id, search); }}
       onFoldersChange={setFolders}
       openFolderRequest={openFolderReq}
       onItemMoved={(id, folderId) => setViewingItem((prev) => (prev && prev.id === id ? { ...prev, folder_id: folderId } : prev))}
       onItemDeleteStart={onItemDeleteStart}
       onItemDeleted={onItemDeleted}
       refreshKey={historyRefreshKey}
       historyEnabled={historyEnabled}
       onEnableHistory={() => updateHistorySettings({ history_enabled: true, history_retention: 'month' })}
       hasOrganization={hasOrganization}
     />
     </div>
     {/* Правее боковой панели (ширина — --sidebar-w, по умолчанию 248px): на
         left-6 плашка ложилась на кнопку меню пользователя, и пока шла
         обработка, в личный кабинет было не попасть (прогон 24.09). Справа
         внизу — тосты «Готово» и «Ответили». */}
     {runningJobs > 0 && activeTab !== 'transcription' && !keyboard.open && (
       <div className="fixed bottom-3 left-3 md:bottom-6 md:left-[calc(var(--sidebar-w,248px)_+_24px)] z-30 rounded-[12px] bg-white px-4 py-2 text-[13px]"
            role="status" style={{ boxShadow: '0 6px 20px rgba(0,0,0,.08)', color: '#5a5a5a' }}>
         Идёт обработка · {runningJobs} — результат ляжет в Историю
       </div>
     )}
     {jobToast && !feedbackOpen && (
       <JobDoneToast
         job={jobToast}
         onOpen={() => { const id = jobToast.item_id; closeJobToast(); if (id) openHistoryItem(id); }}
         onClose={closeJobToast}
       />
     )}
     {answerToast > 0 && !feedbackOpen && !jobToast && (
       <FeedbackAnswerToast
         count={answerToast}
         onOpen={() => { writeShown(window.sessionStorage, answerToast); setAnswerToast(0); setFeedbackOpen(true); }}
         onClose={() => { writeShown(window.sessionStorage, answerToast); setAnswerToast(0); }}
       />
     )}
     <DemoAccessDialog open={demoOpen} onClose={() => setDemoOpen(false)} />
     <FeedbackDialog
       open={feedbackOpen}
       onClose={() => setFeedbackOpen(false)}
       context={{ screen: activeTab, record_id: viewingItem?.id || null }}
       role={tokenData?.role}
       onAnswersRead={refreshUnreadFeedback}
     />
     <div className={`flex-1 min-w-0 flex flex-col overflow-hidden px-3 ${keyboard.open ? 'py-2' : 'pb-3'} md:pl-3 md:pr-7 md:pt-[22px] md:pb-7`}>
       {isMobile && !keyboard.open && (
         <MobileHeader
           onMenu={() => setNavOpen(true)}
           quota={quotaChip}
           onQuota={quotaChip?.canTopUp ? () => { if (canLeaveTo('pricing')) setActiveTab('pricing'); } : undefined}
         />
       )}
       {/* На главном экране телефона строки заголовка нет — как в макете:
           там уже шапка и крупный призыв. Имя записи во время работы остаётся.
           У записи Истории заголовок и так крупно в карточке, у протокола
           место отдано документу — шаблон виден в свёрнутых настройках. */}
       {!keyboard.open && !(isMobile && ((activeTab === 'transcription' && !recordTitle) || ['history', 'protocol'].includes(activeTab))) && (
       <AppTopbar
         compact={isMobile}
         path={activeTab === 'history' ? folderPath : null}
         title={activeTab === 'history'
           ? 'История'
           : tabChrome(activeTab, { titles: TAB_TITLES, hints: TAB_HINTS, recognitionV2: RECOGNITION_V2,
                                    recordTitle: chromeRecordTitle }).title}
         subtitle={activeTab === 'history'
           ? (viewingItem?.title || undefined)
           : tabChrome(activeTab, { titles: TAB_TITLES, hints: TAB_HINTS, recognitionV2: RECOGNITION_V2,
                                    recordTitle: chromeRecordTitle }).subtitle}
         right={(
           /* Вход в обращения стоит справа вверху, как в артборде «App»: он
              нужен на любом экране, а в меню под аватаром его сначала надо было
              найти. Пункт в меню остаётся — там же живут непрочитанные ответы.
              Подпись — общая с пунктом меню, см. lib/feedbackEntry.js. */
           <button
             type="button"
             onClick={() => setFeedbackOpen(true)}
             className={BTN_SECONDARY}
           >
             <MessageSquare size={14} strokeWidth={1.75} />
             {FEEDBACK_ENTRY_LABEL}
             {unreadFeedback > 0 && (
               <span className="rounded-badge bg-paper-accent px-[6px] py-[1px] text-[11px] font-semibold text-white"
                     title="Есть непрочитанные решения">
                 {unreadFeedback}
               </span>
             )}
           </button>
         )}
       />
       )}

       <main className="flex-1 min-h-0 w-full flex flex-col gap-[14px] overflow-y-auto font-montserrat">
           {/* Предупреждение об остатке — только над рабочими вкладками: на метриках
               и в админке оно было бы шумом. Само себя прячет, пока запас есть. */}
           {['transcription', 'dual', 'streaming', 'protocol'].includes(activeTab) && !keyboard.open && (
             <QuotaBanner
               email={tokenData?.email}
               role={tokenData?.role}
               onOpenPricing={() => setActiveTab('pricing')}
             />
           )}
           {/* Единый экран «Распознавание» (спринт 3 редизайна) — за своим
               флагом. Пока он выключен, вкладка работает ровно как прежде:
               бот-участник плюс загрузка файла или ссылки. */}
           {activeTab === 'transcription' && RECOGNITION_V2 && (
             <RecognitionScreen
               resetRequest={{ seq: recognitionResetKey, taskId: recognitionResetTaskId }}
               onOpenProtocol={onOpenProtocol}
               onOpenPricing={() => { if (canLeaveTo('pricing')) setActiveTab('pricing'); }}
               onRecordTitle={setRecordTitle}
               role={tokenData?.role}
               email={tokenData?.email}
               // Боту некуда класть результат при выключенной Истории — экран
               // предупреждает об этом до приглашения и включает её тем же
               // нажатием, а не отказом посреди встречи.
               historyEnabled={historyEnabled}
               onEnableHistory={() => updateHistorySettings({ history_enabled: true, history_retention: 'month' })}
               // Сохранение в Историю живёт здесь, как у остальных экранов:
               // autoSaveItem идемпотентен по ключу задачи и не задвоит запись.
               // Переименование участников после сохранения: текст записи
               // догоняется тем же PATCH, что и у старого экрана. Эффект
               // transcriptUpdate ищет запись по transcriptionState.taskId —
               // поэтому при сохранении кладём туда и ключ задачи, иначе
               // правка осталась бы только на экране.
               onRenamed={(done) => setTranscriptionState((prev) => ({
                 ...prev, rawTranscription: transcriptText(done),
               }))}
               onStarted={registerJob}
               // Запись с микрофона на этом экране — в то же состояние, что и у
               // вкладки «Микрофон»: сохранение в Историю, автоимя и привязку
               // протокола делают эффекты над streamingState.
               onLiveChange={setStreamingState}
               onMeetingDone={() => setHistoryRefreshKey((k) => k + 1)}
               onSaved={({ taskId, done, file, duration, byChannels, title, sourceUrl }) => {
                 // Минуты списаны — остаток в боковой панели перечитывается и
                 // при выключенной Истории, когда autoSaveItem ничего не сохранит.
                 setHistoryRefreshKey((k) => k + 1);
                 setTranscriptionState((prev) => ({
                   ...prev, taskId, rawTranscription: transcriptText(done),
                 }));
                 // task_id в meta — ключ идемпотентности на сервере: если
                 // завершитель доделал задачу раньше вкладки, POST /items
                 // вернёт его запись, а не заведёт вторую.
                 saveRecognized(taskId, {
                   type: byChannels ? 'dual_channel' : 'transcription',
                   title: file?.name || title || 'Распознавание',
                   transcript: transcriptText(done),
                   // source_url — запись по ссылке: История покажет источник
                   // «Ссылка» и сам адрес. Публичная ссылка его не отдаёт —
                   // meta там по белому списку (bit-history/app/share.py).
                   meta: { ...(duration ? { duration } : {}), task_id: taskId,
                           ...(sourceUrl ? { source_url: sourceUrl } : {}) },
                 });
               }}
             />
           )}
           {activeTab === 'transcription' && !RECOGNITION_V2 && (
             <>
               {/* Бот-участник (bit-meetbot): вставить ссылку на встречу вместо
                   загрузки готовой записи — бот сам зайдёт, запишет и после
                   встречи положит в Историю расшифровку с именами участников.
                   Сам себя прячет, пока не выставлен REACT_APP_MEETBOT_ENABLED
                   (адаптер площадки ещё заготовка). */}
               <MeetBotJoin onFinished={() => setHistoryRefreshKey((k) => k + 1)} />
               <TranscriptionWithoutDiarization
                   key={recognitionResetKey}
                   state={transcriptionState}
                   setState={setTranscriptionState}
                   onOpenProtocol={onOpenProtocol}
                   onRenameSpeakers={renameSpeakers}
                   onSpeakerConfirmed={confirmSpeaker}
         />
             </>
       )}
    {activeTab === 'streaming' && (
      <Suspense fallback={<div className="text-center py-8">Загрузка компонента...</div>}>
        <StreamingTranscription
          state={streamingState}
          setState={setStreamingState}
          onOpenProtocol={onOpenProtocol}
        />
      </Suspense>
    )}

           {activeTab === 'dual' && (
  <DualChannelTranscription state={dualState} setState={setDualState} />
)}

       {activeTab === 'protocol' && (
         <MeetingProtocol
           key={protocolResetKey}
           state={protocolState}
           setState={setProtocolState}
           shareItemId={historyEnabled ? protocolRecordId : null}
           // Протокол, собранный для другой записи, экран кладёт в её запись
           // сам; запись свежей расшифровки ищется по задаче распознавания.
           resolveRecordId={(owner) => {
             if (!historyEnabled || !owner) return null;
             const linked = owner.sourceTaskId ? historyLinks[owner.sourceTaskId] : null;
             return (linked && linked !== 'pending') ? linked : (owner.standaloneItemId || null);
           }}
           onSavedElsewhere={() => setHistoryRefreshKey((k) => k + 1)}
           historyEnabled={historyEnabled}
           onEnableHistory={() => updateHistorySettings({ history_enabled: true, history_retention: 'month' })}
           onRenameSpeakers={renameSpeakers}
           onSpeakerConfirmed={confirmSpeaker}
           onGoToRecognition={() => setActiveTab('transcription')}
         />
       )}
           {/* «Метрики» (вход — в меню пользователя) — ЛИЧНЫЕ: остаток пакета, что распознано,
               куда ушло время. Платформенные метрики (вся аудитория, таблица
               людей, разрезы по сервисам) уехали в Админку вкладкой «Метрики
               платформы»: это инструмент администратора, а не человека. */}
           {activeTab === 'metrics' && (
               <UserMetrics
                 email={tokenData?.email}
                 onGoToRecognition={() => setActiveTab('transcription')}
                 onGoToPricing={PricingPage ? () => setActiveTab('pricing') : null}
               />
           )}
          {activeTab === 'tts' && (
              <TTSPage />
          )}
           {/* Тарифы и оплата — только издание saas. PricingPage тянет за собой
               TopupDialog и обращения к bit-billing, которого у клиента нет.
               Переменная читается здесь же: только так терсер выкинет ветку
               вместе с import() (см. publicRoutes.js). */}
           {activeTab === 'pricing' && PricingPage && (
             <>
               {paymentReturn && <PaymentReturnBanner onDone={() => setPaymentReturn(false)} />}
               <Suspense fallback={<div className="py-16" />}>
                 <PricingPage role={tokenData?.role} />
               </Suspense>
             </>
           )}
           {activeTab === 'organization' && (
               <OrganizationPanel onOpenPricing={PricingPage ? () => setActiveTab('pricing') : null}
                                  showEmpty onMembershipChange={onMembershipChange} />
           )}
           {activeTab === 'profile' && (
               <ProfilePage onBack={() => setActiveTab(previousTab)} onHistoryChanged={reloadHistorySettings} onOpenMetrics={() => setActiveTab('metrics')}
                            onOpenPricing={PricingPage ? () => setActiveTab('pricing') : null}
                            onOpenOrganization={PRICING_ENABLED && isOrgOwner ? openOrganizationTab : null} />
           )}
           {activeTab === 'admin' && (
               <AdminPanel onBack={() => setActiveTab(previousTab)} />
           )}
           {activeTab === 'history' && (
               <HistoryViewer key={viewingSearch ? `${viewingItem?.id}:${viewingSearch.seq}` : viewingItem?.id} item={viewingItem} search={viewingSearch} onBack={() => setActiveTab(previousTab)} onShareChanged={() => setHistoryRefreshKey((k) => k + 1)} onItemUpdated={(fields) => { const id = viewingItem?.id; setViewingItem((prev) => (prev && prev.id === id ? { ...prev, ...fields } : prev)); setHistoryRefreshKey((k) => k + 1); }} onCreateProtocol={onProtocolFromHistory} folderPath={folderPath} />
           )}
     </main>
     </div>
     {/* Окно согласия с офертой: только в saas-издании, у on-prem оферты нет. */}
     {IS_SAAS && <LegalGate onLogout={handleLogout} />}
   </div>
 );
};

export default App;